export function countCodepoints(text) {
    let n = 0;
    for (const _ of text)
        n++;
    return n;
}
export function countUtf16Units(text) {
    return text.length;
}
export function countUtf8Bytes(text) {
    let bytes = 0;
    for (const ch of text) {
        const cp = ch.codePointAt(0);
        if (cp <= 0x7f)
            bytes += 1;
        else if (cp <= 0x7ff)
            bytes += 2;
        else if (cp <= 0xffff)
            bytes += 3;
        else
            bytes += 4;
    }
    return bytes;
}
export function measureLength(text, measure) {
    switch (measure) {
        case 'utf16':
            return countUtf16Units(text);
        case 'bytes':
            return countUtf8Bytes(text);
        default:
            return countCodepoints(text);
    }
}
const HASHTAG_RUN_RE = /(?:^|[^\p{L}\p{N}_])((?:#[\p{L}\p{N}_]+)+)/gu;
const MENTION_RUN_RE = /(?:^|[^\p{L}\p{N}_])((?:@[\p{L}\p{N}_]+)+)/gu;
export function countMarkedTokens(text, marker) {
    const re = marker === '#' ? HASHTAG_RUN_RE : MENTION_RUN_RE;
    let total = 0;
    for (const match of text.matchAll(re)) {
        for (const ch of match[1])
            if (ch === marker)
                total++;
    }
    return total;
}
const PINTEREST_LIMITS = {
    platform: 'pinterest',
    displayName: 'Pinterest',
    source: 'Pinterest REST API v5.28.0 OpenAPI, PinCreate schema (verified 2026-09-05)',
    fields: [
        {
            contentField: 'title',
            label: 'title',
            max: 100,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'title',
        },
        {
            contentField: 'description',
            label: 'description',
            max: 800,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'description',
        },
        {
            contentField: 'altText',
            label: 'alt text',
            max: 500,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'alt_text',
        },
        {
            contentField: 'link',
            label: 'link',
            max: 2048,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'link',
        },
    ],
};
const INSTAGRAM_LIMITS = {
    platform: 'instagram',
    displayName: 'Instagram',
    source: 'Meta Instagram Platform API reference, IG User /media `caption` parameter (verified 2026-09-05)',
    fields: [
        {
            contentField: 'caption',
            label: 'caption',
            max: 2200,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'caption',
        },
    ],
    tokens: [
        { contentField: 'caption', label: 'caption', max: 30, marker: '#', unit: 'hashtags' },
        { contentField: 'caption', label: 'caption', max: 20, marker: '@', unit: '@ mentions' },
    ],
};
const LINKEDIN_LIMITS = {
    platform: 'linkedin',
    displayName: 'LinkedIn',
    source: 'LinkedIn UGC Post API docs — "maximum length of the text of a UGC Post is 3,000 characters" (verified 2026-09-05)',
    fields: [
        {
            contentField: 'caption',
            label: 'post text',
            max: 3000,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'commentary',
        },
    ],
};
const TIKTOK_LIMITS = {
    platform: 'tiktok',
    displayName: 'TikTok',
    source: 'TikTok Content Posting API (Direct Post), post_info.title — "maximum length is 2200 in UTF-16 runes" (verified 2026-09-05)',
    fields: [
        {
            contentField: 'caption',
            label: 'caption',
            max: 2200,
            measure: 'utf16',
            unit: 'characters',
            providerField: 'post_info.title',
        },
    ],
};
const YOUTUBE_LIMITS = {
    platform: 'youtube_shorts',
    displayName: 'YouTube Shorts',
    source: 'YouTube Data API v3 `videos` resource, snippet.title / .description / .tags (verified 2026-09-05)',
    fields: [
        {
            contentField: 'title',
            label: 'title',
            max: 100,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'snippet.title',
        },
        {
            contentField: 'description',
            label: 'description',
            max: 5000,
            measure: 'bytes',
            unit: 'bytes',
            providerField: 'snippet.description',
        },
        {
            contentField: 'hashtags',
            label: 'hashtags',
            max: 500,
            measure: 'codepoints',
            unit: 'characters',
            providerField: 'snippet.tags',
        },
    ],
};
const FACEBOOK_LIMITS = {
    platform: 'facebook',
    displayName: 'Facebook',
    source: 'Meta Graph API publishes no documented maximum for Page feed `message` (verified 2026-09-05)',
    fields: [],
};
const X_LIMITS = {
    platform: 'x',
    displayName: 'X',
    source: 'Handled by existing truncation (X_TWEET_MAX_LENGTH); X publishes no API maxLength (verified 2026-09-05)',
    fields: [],
};
export const PLATFORM_CONTENT_LIMITS = {
    pinterest: PINTEREST_LIMITS,
    instagram: INSTAGRAM_LIMITS,
    linkedin: LINKEDIN_LIMITS,
    tiktok: TIKTOK_LIMITS,
    youtube_shorts: YOUTUBE_LIMITS,
    facebook: FACEBOOK_LIMITS,
    x: X_LIMITS,
};
export function platformFieldMax(platform, contentField) {
    const limit = PLATFORM_CONTENT_LIMITS[platform]?.fields.find(f => f.contentField === contentField);
    return limit ? limit.max : null;
}
const FORBIDDEN_CHARACTERS = {
    youtube_shorts: { chars: ['<', '>'], contentFields: ['title', 'description'] },
};
const HASHTAG_CAP = {
    instagram: 5,
    tiktok: 5,
};
const HASHTAG_SEPARATOR = {
    tiktok: ' ',
};
const TRAILING_HASHTAGS_RE = /(?:\s*#[\p{L}\p{N}_]+)+\s*$/u;
function stripTrailingHashtagBlock(text, hashtags) {
    if (hashtags.length === 0)
        return text;
    return text.replace(TRAILING_HASHTAGS_RE, '').trimEnd();
}
function readString(content, key) {
    const value = content[key];
    return typeof value === 'string' ? value : '';
}
function readHashtags(content) {
    const value = content.hashtags;
    if (!Array.isArray(value))
        return [];
    return value.filter((h) => typeof h === 'string');
}
const BODY_FIELD = {
    instagram: 'caption',
    linkedin: 'caption',
    tiktok: 'caption',
    facebook: 'message',
    x: 'text',
    youtube_shorts: 'description',
    pinterest: 'description',
};
export function resolvePlatformPublishText(platform, contentField, content) {
    const raw = readString(content, contentField);
    if (contentField === 'hashtags') {
        return readHashtags(content)
            .map(h => h.replace(/^#/, ''))
            .join(',');
    }
    if (contentField !== BODY_FIELD[platform])
        return raw;
    const hashtags = readHashtags(content);
    if (platform === 'facebook' || platform === 'pinterest')
        return raw;
    if (hashtags.length === 0)
        return raw;
    const cap = HASHTAG_CAP[platform];
    const tags = (cap ? hashtags.slice(0, cap) : hashtags).map(h => (h.startsWith('#') ? h : `#${h}`));
    const body = stripTrailingHashtagBlock(raw, hashtags);
    const separator = HASHTAG_SEPARATOR[platform] ?? '\n\n';
    const parts = [];
    if (body)
        parts.push(body);
    if (tags.length)
        parts.push(tags.join(' '));
    return parts.join(separator);
}
function formatCount(n) {
    return n.toLocaleString('en-US');
}
export function validatePlatformContent(platform, content) {
    const limits = PLATFORM_CONTENT_LIMITS[platform];
    if (!limits || !content)
        return { ok: true, violations: [] };
    const violations = [];
    const { displayName } = limits;
    for (const field of limits.fields) {
        const text = resolvePlatformPublishText(platform, field.contentField, content);
        if (!text)
            continue;
        const actual = measureLength(text, field.measure);
        if (actual <= field.max)
            continue;
        const over = actual - field.max;
        violations.push({
            platform,
            contentField: field.contentField,
            rule: 'length',
            actual,
            limit: field.max,
            unit: field.unit,
            message: `${displayName} won't accept this post: the ${field.label} is ` +
                `${formatCount(actual)} ${field.unit} and ${displayName} allows ${formatCount(field.max)}. ` +
                `Shorten the ${field.label} by ${formatCount(over)} ${field.unit}.`,
        });
    }
    for (const token of limits.tokens ?? []) {
        const text = resolvePlatformPublishText(platform, token.contentField, content);
        if (!text)
            continue;
        const actual = countMarkedTokens(text, token.marker);
        if (actual <= token.max)
            continue;
        const over = actual - token.max;
        violations.push({
            platform,
            contentField: token.contentField,
            rule: 'tokens',
            actual,
            limit: token.max,
            unit: token.unit,
            message: `${displayName} won't accept this post: the ${token.label} has ` +
                `${formatCount(actual)} ${token.unit} and ${displayName} allows ${formatCount(token.max)}. ` +
                `Remove ${formatCount(over)} so the ${token.label} has at most ` +
                `${formatCount(token.max)} ${token.unit}.`,
        });
    }
    const forbidden = FORBIDDEN_CHARACTERS[platform];
    if (forbidden) {
        for (const contentField of forbidden.contentFields) {
            const text = resolvePlatformPublishText(platform, contentField, content);
            if (!text)
                continue;
            const present = forbidden.chars.filter(ch => text.includes(ch));
            if (present.length === 0)
                continue;
            const limitDef = limits.fields.find(f => f.contentField === contentField);
            const label = limitDef?.label ?? contentField;
            violations.push({
                platform,
                contentField,
                rule: 'characters',
                actual: present.length,
                limit: 0,
                unit: 'characters',
                message: `${displayName} won't accept this post: the ${label} contains ` +
                    `${present.map(c => `"${c}"`).join(' and ')}, which ${displayName} rejects. ` +
                    `Remove ${present.length > 1 ? 'those characters' : 'that character'}.`,
            });
        }
    }
    return violations.length ? { ok: false, violations } : { ok: true, violations: [] };
}
export function formatPlatformContentViolations(violations) {
    if (violations.length === 0)
        return '';
    if (violations.length === 1)
        return violations[0].message;
    return violations.map(v => v.message).join('\n');
}
export const OUT_OF_SCOPE_NOTES = Object.freeze([
    'facebook:length',
    'x:length',
    'instagram:alt_text',
    'media-constraints',
    'account-state',
    'rate-limits',
]);
//# sourceMappingURL=platform-content-limits.js.map